<template>
  <div class="captcha-wrapper">
    <vue-recaptcha
      v-if="shouldLoadRecaptchaScript"
      ref="recaptchaInner"
      :sitekey="recaptchaSitekey"
      :load-recaptcha-script="true"
      size="invisible"
      @verify="onCaptchaVerify"
      @expired="onCaptchaExpire"
      @error="onRecaptchaError"
    />
  </div>
</template>

<script>
import { VueRecaptcha } from 'vue-recaptcha'
import * as Bugsnag from '@bugsnag/js'

export default {
  name: 'CaptchaBlock',
  components: {
    'vue-recaptcha': VueRecaptcha,
  },
  props: {
    keyType: { type: String, required: true },
    deferScriptLoad: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      shouldLoadRecaptchaScript: !this.deferScriptLoad,
      captchaRunRetries: 0,
      captchaRetryTimeoutId: null,
    }
  },
  computed: {
    recaptchaSitekey() {
      switch (this.keyType) {
        case 'login':
          return process.env.VUE_APP_INVISIBLE_RECAPTCHA_SITEKEY
        case 'subscriptions':
          return process.env.VUE_APP_INVISIBLE_SUBSCRIPTIONS_RECAPTCHA_SITEKEY
        default:
          return ''
      }
    },
  },
  methods: {
    execute() {
      this.$store.dispatch('app/setIsLoadingCaptcha', true)
      if (this.deferScriptLoad && !this.shouldLoadRecaptchaScript) {
        this.shouldLoadRecaptchaScript = true
        this.$nextTick(() => {
          this.runCaptcha()
        })
        return
      }
      this.runCaptcha()
    },
    runCaptcha() {
      const widget = this.$refs.recaptchaInner
      if (!widget || typeof widget.execute !== 'function') {
        this.retryCaptchaExecution({
          details: 'Recaptcha widget not ready',
        })
        return
      }
      try {
        widget.execute()
        this.captchaRunRetries = 0
      } catch (error) {
        const details = error ? String(error) : 'Recaptcha execute failed'
        const hasNotLoadedYet =
          details.includes('ReCAPTCHA has not been loaded') ||
          details.includes('Recaptcha has not been loaded')
        if (hasNotLoadedYet) {
          this.retryCaptchaExecution({ details })
          return
        }
        this.$store.dispatch('app/setIsLoadingCaptcha', false)
        this.sendBugsnagNotification({
          stage: 'error',
          details,
        })
      }
    },
    retryCaptchaExecution({ details }) {
      const maxRetries = 30
      const retryDelayMs = 100
      if (this.captchaRunRetries >= maxRetries) {
        this.$store.dispatch('app/setIsLoadingCaptcha', false)
        this.sendBugsnagNotification({
          stage: 'not_ready',
          details,
        })
        return
      }
      this.captchaRunRetries += 1
      if (this.captchaRetryTimeoutId) {
        clearTimeout(this.captchaRetryTimeoutId)
      }
      this.captchaRetryTimeoutId = setTimeout(() => {
        this.runCaptcha()
      }, retryDelayMs)
    },
    onCaptchaVerify(token) {
      this.$store.dispatch('app/setIsLoadingCaptcha', false)
      // Do not send the token to Bugsnag; treat it as a secret
      this.sendBugsnagNotification({ stage: 'verified' })
      this.$emit('captcha-verify-action', { recaptcha: token })
    },
    onCaptchaExpire() {
      this.$store.dispatch('app/setIsLoadingCaptcha', false)
      this.sendBugsnagNotification({ stage: 'expired' })
    },
    onRecaptchaError(error) {
      this.$store.dispatch('app/setIsLoadingCaptcha', false)
      this.sendBugsnagNotification({
        stage: 'error',
        details: error ? String(error) : 'reCAPTCHA failed to load',
      })
    },
    // Send a Bugsnag notification for reCAPTCHA lifecycle events
    // IMPORTANT: never send the token to Bugsnag (treat it as secret)
    sendBugsnagNotification({ stage, details } = {}) {
      const notificationData = {
        message: `reCAPTCHA ${stage}`,
        data: {
          stage,
          details: details || null,
          timestamp: new Date().toISOString(),
        },
      }

      Bugsnag.addMetadata('recaptcha_event_data', notificationData)
      Bugsnag.notify(JSON.stringify(notificationData))
    },
  },
  beforeUnmount() {
    if (this.captchaRetryTimeoutId) {
      clearTimeout(this.captchaRetryTimeoutId)
      this.captchaRetryTimeoutId = null
    }
  },
}
</script>
